import React from 'react';
import styled from 'styled-components';

const StepsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  padding-right: 11px;
`;

const StepItem = styled.div<{ clickable: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-right: -11px;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
`;

const StepCircle = styled.div<{
  active: boolean;
  completed: boolean;
  clickable: boolean;
}>`
  width: 37px;
  height: 37px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-family);
  font-weight: 500;
  font-size: 13px;
  line-height: 1.5;
  background: ${({ active, completed }) =>
    active || completed ? 'var(--red-dark)' : 'var(--white-main)'};
  color: ${({ active, completed }) =>
    active || completed ? 'var(--white-main)' : 'var(--black-main)'};
  border: ${({ active, completed }) =>
    active || completed ? 'none' : '1px solid var(--gray-dark)'};
  position: relative;
  z-index: 1;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  transition: opacity 0.2s ease;

  ${({ clickable }) =>
    clickable &&
    `
    &:hover {
      opacity: 0.8;
    }
  `}
`;

const CheckmarkIcon = styled.i`
  font-size: 11px;
  color: var(--white-main);
`;

const StepLabel = styled.p`
  font-family: var(--font-family);
  font-weight: 500;
  font-size: 11px;
  line-height: 1.5;
  color: var(--black-main);
  text-align: center;
  width: 81px;
  margin: 0;
  white-space: pre-wrap;
`;

const StepConnector = styled.div`
  height: 37px;
  width: 40px;
  margin-right: -11px;
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  padding-top: 0;

  &::after {
    content: '';
    position: absolute;
    top: 18.5px;
    left: 0;
    right: 0;
    height: 1px;
    background: var(--gray-dark);
  }
`;

export type StepState = {
  number: number;
  label: string;
  active: boolean;
  completed: boolean;
  clickable?: boolean;
  step?: string;
};

export type CustomizeCourseStepsIndicatorProps = {
  steps: StepState[];
  onStepClick?: (step: string) => void;
};

export const CustomizeCourseStepsIndicator: React.FC<
  CustomizeCourseStepsIndicatorProps
> = ({ steps, onStepClick }) => {
  const handleStepClick = (step: StepState) => {
    if (step.clickable && step.step && onStepClick) {
      onStepClick(step.step);
    }
  };

  return (
    <StepsContainer>
      {steps.map((step, index) => (
        <React.Fragment key={step.number}>
          <StepItem
            clickable={step.clickable || false}
            onClick={() => handleStepClick(step)}
          >
            <StepCircle
              active={step.active}
              completed={step.completed}
              clickable={step.clickable || false}
            >
              {step.completed ? (
                <CheckmarkIcon className="pi pi-check" />
              ) : (
                step.number
              )}
            </StepCircle>
            <StepLabel>{step.label}</StepLabel>
          </StepItem>
          {index < steps.length - 1 && <StepConnector />}
        </React.Fragment>
      ))}
    </StepsContainer>
  );
};
